import API from "../API";
import { listarRepsPrueba } from "./pruebaReps";

function celdaCsv(v) {
    const s = String(v ?? "");
    if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

function filaCsv(valores) {
    return valores.map(celdaCsv).join(",");
}

function formatoCelda(v) {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (Number.isFinite(n)) return n.toFixed(2);
    return String(v);
}

/** Hasta 6 decimales; conserva valores pequeños que con 2 decimales se verían como 0. */
function formatoDesviacionMuestra(desv) {
    if (!Number.isFinite(desv) || desv === 0) return "0";
    const texto = desv.toFixed(6).replace(/\.?0+$/, "");
    return texto || "0";
}

/** Media ± desviación estándar, p. ej. `555.00±40` o `0.30±0.0041`. */
function formatoMuestraParametro(p) {
    const valor = Number(p?.valor);
    if (!Number.isFinite(valor)) return "";
    const valorStr = valor.toFixed(2);
    const desvStr = formatoDesviacionMuestra(Number(p?.desviacion));
    return `${valorStr}±${desvStr}`;
}

function expandirColumnasPrueba(doc) {
    const base = {
        nombre: doc?.nombre ?? "",
        identificacion: doc?.ide ?? "",
        pesoSujeto: doc?.peso ?? "",
        notas: doc?.impresion ?? "",
    };

    if (Array.isArray(doc?.reps) && doc.reps.length > 0) {
        return doc.reps.map((rep, i) => ({
            ...base,
            repeticion: rep.label ?? `Rep. ${i + 1}`,
            parametros: rep.parametros ?? [],
        }));
    }

    const reps = listarRepsPrueba(doc);
    return [
        {
            ...base,
            repeticion: reps[0]?.label ?? "Rep. 1",
            parametros: doc?.parametros ?? [],
        },
    ];
}

function expandirColumnasMuestra(doc) {
    return [
        {
            nombre: doc?.nombre ?? "",
            identificacion: "",
            pesoSujeto: "",
            repeticion: "",
            notas: doc?.nota ?? "",
            parametros: doc?.parametros ?? [],
        },
    ];
}

async function obtenerDocumentos(registros, isMuestra) {
    const endpoint = isMuestra ? "muestra" : "prueba";
    const docs = await Promise.all(
        registros.map(async (id) => {
            const { data } = await API(`${endpoint}/${id}`);
            return data.doc;
        })
    );
    return docs;
}

function etiquetaColumna(index, isMuestra) {
    return isMuestra ? `Muestra ${index + 1}` : `Atleta ${index + 1}`;
}

function buildFilasComparacionMuestras(columnas) {
    const filas = [
        filaCsv(["Parámetro", ...columnas.map((_, i) => etiquetaColumna(i, true))]),
    ];

    const ordenParametros = [];
    const vistos = new Set();
    for (const col of columnas) {
        for (const p of col.parametros ?? []) {
            const nombre = p?.nombre;
            if (nombre && !vistos.has(nombre)) {
                vistos.add(nombre);
                ordenParametros.push(nombre);
            }
        }
    }

    const filaMeta = (etiqueta, extractor) =>
        filaCsv([etiqueta, ...columnas.map(extractor)]);

    filas.push(filaMeta("Nombre", (c) => c.nombre));

    for (const nombreParam of ordenParametros) {
        filas.push(
            filaCsv([
                nombreParam,
                ...columnas.map((col) => {
                    const p = (col.parametros ?? []).find((x) => x.nombre === nombreParam);
                    return p ? formatoMuestraParametro(p) : "";
                }),
            ])
        );
    }

    filas.push(filaMeta("Notas", (c) => c.notas));
    return filas;
}

function buildFilasComparacionPruebas(columnas) {
    const filas = [
        filaCsv(["Parámetro", ...columnas.map((_, i) => etiquetaColumna(i, false))]),
    ];

    const ordenParametros = [];
    const vistos = new Set();
    for (const col of columnas) {
        for (const p of col.parametros ?? []) {
            const nombre = p?.nombre;
            if (nombre && !vistos.has(nombre)) {
                vistos.add(nombre);
                ordenParametros.push(nombre);
            }
        }
    }

    const filaMeta = (etiqueta, extractor) =>
        filaCsv([etiqueta, ...columnas.map(extractor)]);

    filas.push(filaMeta("Nombre", (c) => c.nombre));
    filas.push(filaMeta("Identificación", (c) => c.identificacion));
    filas.push(filaMeta("Peso Sujeto", (c) => formatoCelda(c.pesoSujeto)));
    filas.push(filaMeta("Repetición", (c) => c.repeticion));

    for (const nombreParam of ordenParametros) {
        filas.push(
            filaCsv([
                nombreParam,
                ...columnas.map((col) => {
                    const p = (col.parametros ?? []).find((x) => x.nombre === nombreParam);
                    return p ? formatoCelda(p.valor) : "";
                }),
            ])
        );
    }

    filas.push(filaMeta("Notas", (c) => c.notas));
    return filas;
}

/**
 * CSV comparativo: filas de parámetro/metadatos y una columna por rep de cada registro seleccionado.
 */
export async function buildCsvComparacionParametros(registros, { isMuestra = false } = {}) {
    if (!registros?.length) return null;

    const docs = await obtenerDocumentos(registros, isMuestra);
    const columnas = docs.flatMap((doc) =>
        isMuestra ? expandirColumnasMuestra(doc) : expandirColumnasPrueba(doc)
    );

    if (!columnas.length) return null;

    const filas = isMuestra
        ? buildFilasComparacionMuestras(columnas)
        : buildFilasComparacionPruebas(columnas);

    return filas.join("\n");
}

export async function blobCsvComparacionParametros(registros, opts = {}) {
    const csv = await buildCsvComparacionParametros(registros, opts);
    if (!csv) return null;
    return new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
}

export function descargarBlobCsv(blob, nombreArchivo) {
    const href = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", nombreArchivo);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(href);
}
